/**
 * Portfolio Summary Report Helper Utilities
 * Provides formatting, calculation, and data manipulation functions
 */

/**
 * Format currency values with Indian locale
 * @param {number|string} value - The value to format
 * @param {object} options - Formatting options
 * @returns {string} Formatted currency string (₹ 12,34,567.00)
 */
export const formatCurrency = (value, options = {}) => {
  const {
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    locale = 'en-IN',
  } = options;

  if (value === null || value === undefined) {
    return '₹ 0.00';
  }

  const numValue = Number(value);
  if (isNaN(numValue)) {
    return '₹ 0.00';
  }

  // Format with locale
  const formatted = numValue.toLocaleString(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  });

  // Add rupee symbol
  return `₹ ${formatted}`;
};

/**
 * Format percentage values
 * @param {number|string} value - The value to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted percentage string (12.64%)
 */
export const formatPercentage = (value, decimals = 2) => {
  if (value === null || value === undefined) {
    return '0.00%';
  }

  const numValue = Number(value);
  if (isNaN(numValue)) {
    return '0.00%';
  }

  return `${numValue.toFixed(decimals)}%`;
};

/**
 * Determine CSS class for gain/loss coloring
 * @param {number} value - The gain/loss value
 * @returns {string} CSS class name ('positive', 'negative', or 'neutral')
 */
export const getGainLossClass = (value) => {
  if (value === null || value === undefined) return 'neutral';
  const numValue = Number(value);
  if (isNaN(numValue)) return 'neutral';
  
  if (numValue > 0) return 'positive';
  if (numValue < 0) return 'negative';
  return 'neutral';
};

/**
 * Get color for gain/loss value
 * @param {number} value - The gain/loss value
 * @returns {string} Color class ('text-success', 'text-danger', 'text-muted')
 */
export const getGainLossColor = (value) => {
  const cls = getGainLossClass(value);
  
  switch (cls) {
    case 'positive':
      return 'psr-gain-positive';
    case 'negative':
      return 'psr-gain-negative';
    default:
      return 'psr-gain-neutral';
  }
};

/**
 * Validate portfolio summary report data structure
 * @param {object} data - Report data from API
 * @returns {boolean} True if data structure is valid
 */
export const isValidReportData = (data) => {
  if (!data || typeof data !== 'object') {
    return false;
  }

  return (
    Array.isArray(data.asset_groups) &&
    typeof data.grand_total === 'object' &&
    data.grand_total !== null
  );
};

/**
 * Calculate total for a specific asset group
 * @param {array} items - Array of asset items
 * @returns {object} Totals for the group
 */
export const calculateGroupTotal = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) {
    return {
      invested_amount: 0,
      current_value: 0,
      gain_loss: 0,
      gain_loss_percent: 0,
    };
  }

  const totals = items.reduce(
    (acc, item) => {
      acc.invested_amount += Number(item.invested_amount) || 0;
      acc.current_value += Number(item.current_value) || 0;
      acc.gain_loss += Number(item.gain_loss) || 0;
      return acc;
    },
    { invested_amount: 0, current_value: 0, gain_loss: 0 }
  );

  // Calculate percentage
  if (totals.invested_amount > 0) {
    totals.gain_loss_percent = (
      (totals.gain_loss / totals.invested_amount) * 100
    ).toFixed(2);
  } else {
    totals.gain_loss_percent = 0;
  }

  return totals;
};

/**
 * Handle PDF download with proper file naming
 * @param {Blob} pdfBlob - PDF blob from API
 * @param {string} prefix - File name prefix (default: 'Portfolio_Summary_Report')
 */
export const downloadPDF = (pdfBlob, prefix = 'Portfolio_Summary_Report') => {
  if (!pdfBlob || !(pdfBlob instanceof Blob)) {
    throw new Error('Invalid PDF blob');
  }

  const url = window.URL.createObjectURL(pdfBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${prefix}_${new Date().toISOString().split('T')[0]}.pdf`;

  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Generate empty state message
 * @param {boolean} hasError - Whether there's an error
 * @param {boolean} isEmpty - Whether data is empty
 * @returns {object} Object with title and message
 */
export const getEmptyStateMessage = (hasError = false, isEmpty = false) => {
  if (hasError) {
    return {
      title: 'Unable to Load Report',
      message: 'There was an error loading the portfolio summary report. Please try again.',
    };
  }

  if (isEmpty) {
    return {
      title: 'No Records Found',
      message: 'No portfolio data available. Please ensure you have added assets to your portfolio.',
    };
  }

  return {
    title: 'No Data',
    message: 'Unable to retrieve portfolio summary report.',
  };
};

/**
 * Get percentage allocation of an asset group
 * @param {object} groupTotal - Group total object
 * @param {number} grandTotal - Grand total invested amount
 * @returns {string} Percentage allocation formatted
 */
export const getGroupAllocationPercentage = (groupTotal = {}, grandTotal = 0) => {
  if (grandTotal <= 0) return '0.00%';

  const invested = Number(groupTotal.invested_amount) || 0;
  const percentage = ((invested / grandTotal) * 100).toFixed(2);

  return `${percentage}%`;
};

export default {
  formatCurrency,
  formatPercentage,
  getGainLossClass,
  getGainLossColor,
  isValidReportData,
  calculateGroupTotal,
  downloadPDF,
  getEmptyStateMessage,
  getGroupAllocationPercentage,
};
